export type QuestionnairePageType = QuestionnairePage['type']

export type QuestionnairePage =
  | TextPage
  | OptionsPage
  | FinishingSetupPage
  | ReviewPage
  | StatementsPage
  | BooksPage
  | DailyGoalPage
  | NotificationsPage
  | CommitmentPactPage
  | Welcome16167Page
  | Splitscreen16167Page
  | Upgrade16167Page
  | GenreSelection16167Page
  | BookSelection16167Page
  | Loading16167Page
  | Summary16167Page
  | MillionUsers16167Page
  | ProductDemo16167Page

export interface BaseQuestionnairePage {
  type: QuestionnairePageType
  id: string
  title: string
  variableTitle?: string
  subtitle?: string
  image?: QuestionnaireImage
  animation?: QuestionnaireAnimation
  content?: any[]
  hideContinueButton?: boolean
  showLogo?: boolean
}

export interface TextPage extends BaseQuestionnairePage {
  type: 'Text'
  imageAnimation?: 'slide-up' | 'wave'
  textAlignment?: 'left' | 'center'
  textSize?: 'normal' | 'large'
}

export interface OptionsPage extends BaseQuestionnairePage {
  type: 'Options'
  options: QuestionnaireFormOption[]
  multipleOptions?: boolean
  maxOptions?: number
  wrapInputs?: boolean
  centerText?: boolean
}

export interface FinishingSetupPage extends BaseQuestionnairePage {
  type: 'FinishingSetup'
  titleLoading: string
  hideContinueButton: true
  steps: FinishingSetupPageSteps[]
}

export interface ReviewPage extends BaseQuestionnairePage {
  type: 'Review'
  review: string
  reviewer: string
}

export interface StatementsPage extends BaseQuestionnairePage {
  type: 'Statements'
  hideContinueButton: true
  content: string[]
}

export interface BooksPage extends BaseQuestionnairePage {
  type: 'Books'
  content: string[]
}

export interface DailyGoalPage extends BaseQuestionnairePage {
  type: 'DailyGoal'
}

export interface NotificationsPage extends BaseQuestionnairePage {
  type: 'Notifications'
  options: QuestionnaireNotificationsOption[]
}

export interface CommitmentPactPage extends BaseQuestionnairePage {
  type: 'CommitmentPact'
  textInitial: string
  textFinal: string
}

export interface Welcome16167Page extends BaseQuestionnairePage {
  type: 'Welcome16167'
  introTitle: string
  image: QuestionnaireImage
  hideContinueButton: true
}

export interface Splitscreen16167Page extends BaseQuestionnairePage {
  type: 'Splitscreen16167'
  topText: string[]
  bottomText: string[]
  animation: QuestionnaireAnimation
  hideContinueButton: true
}

export interface Upgrade16167Page extends BaseQuestionnairePage {
  type: 'Upgrade16167'
  sectionTitles: string[]
  sectionSubtitles: string[]
  animation: QuestionnaireAnimation
  hideContinueButton: true
}

export interface GenreSelection16167Page extends BaseQuestionnairePage {
  type: 'GenreSelection16167'
  options: {
    id: Questionnaire16167Genre
    icon: string
    label: string
    color: string
  }[]
}

export interface BookSelection16167Page extends BaseQuestionnairePage {
  type: 'BookSelection16167'
  hideContinueButton: true
}

export interface Loading16167Page extends BaseQuestionnairePage {
  type: 'Loading16167'
  titleDone: string
  titleError: string
  animation: QuestionnaireAnimation
  hideContinueButton: true
}

export interface Summary16167Page extends BaseQuestionnairePage {
  type: 'Summary16167'
}

export interface MillionUsers16167Page extends BaseQuestionnairePage {
  type: 'MillionUsers16167'
  content: {
    name: string
    subtitle: string
    image: string
    text: string
  }[]
  animation: QuestionnaireAnimation
  hideContinueButton: true
}

export interface ProductDemo16167Page extends BaseQuestionnairePage {
  type: 'ProductDemo16167'
  content: string[]
  animationMobile: QuestionnaireAnimation
  animationDesktop: QuestionnaireAnimation
  hideContinueButton: true
}

export const QUESTIONNAIRE_GENRES = [
  'communication',
  'relationships',
  'productivity',
  'business',
  'leadership',
  'health',
] as const

export type Questionnaire16167Genre = (typeof QUESTIONNAIRE_GENRES)[number]

export interface Questionnaire16167Book {
  id: string
  title: string
  cover: string
  popularity: number
  genre: Questionnaire16167Genre[]
}

export type QuestionnaireChoice =
  | OptionsChoice
  | StatementsChoice
  | FinishingSetupChoice
  | NotificationsChoice
  | DailyGoalChoice
  | BooksChoice
  | GenreSelection16167Choice
  | BookSelection16167Choice

export type OptionsChoice = string | string[]
export type StatementsChoice = string[]
export type FinishingSetupChoice = string[]
export type NotificationsChoice = string[]
export type DailyGoalChoice = number
export type BooksChoice = {
  id: string
  score: number
}[]
export type GenreSelection16167Choice = Questionnaire16167Genre[]
export type BookSelection16167Choice = {
  id: string
  title: string
  cover: string
}[]

export type TrialStep = {
  text: string
  icon: string
  day: string
}

export type QuestionnaireUserChoices = Record<string, QuestionnaireChoice>

export interface QuestionnaireImage {
  src: string
  width: number
  height: number
}

export interface QuestionnaireAnimation extends QuestionnaireImage {
  stateMachines?: string | string[]
}

export interface QuestionnaireFormOption {
  label: string
  icon?: string
  type?: 'text-input' | 'skip'
  placeholder?: string
  hint?: string
}

export interface FinishingSetupPageSteps {
  title: string
  titleLoading: string
  question: string
}

export interface QuestionnaireNotificationsOption {
  title: string
  subtitle: string
}

export interface QuestionnaireSection {
  id: string
  title: string
  pages: QuestionnairePage[]
}

export interface OnboardingIntroPage {
  title: string
  image: string
  subtitle?: string
}

export interface OnboardingTrialPage {
  title: string
  image?: QuestionnaireImage
  steps?: TrialStep[]
}
